import React, { useEffect, useState, useRef, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useStores } from "src/stores";
import { useServices } from "src/services";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import {
  Grid,
  GridColumn,
  GridToolbar
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import {
  ColumnMenu,
  ColumnMenuCheckboxFilter,
} from "src/components/GridColumnMenus";
import moment from 'moment-timezone';

interface DealerSurveyResponse {
  survey_id: number;
  timestamp: string;
  q1_planned_with_other_activity: 'Yes' | 'No';
  q1_what_event?: string;
  q1_why_not?: string;
  q2_rating_ease_working_charity: '1' | '2' | '3' | '4' | '5';
  q2_rating_explanation?: string;
  q3_program_guides: boolean;
  q3_downloadable_materials: boolean;
  q3_kit_materials: boolean;
  q3_program_giveaways: boolean;
  q3_other_support?: string;
  q3_other_explanation?: string;
  q4_additional_support?: string;
  q5_fundraising_goal_support?: string;
  q6_satisfaction_guidelines?: '1' | '2' | '3' | '4' | '5';
  q6_rating_local_affiliate?: 'Excellent' | 'Good' | 'Neutral' | 'Poor' | 'Very Poor' | 'N/A';
  q6_rating_explanation?: string;
  q7_challenges_benefits?: string;
  q8_overall_value_rating: '1' | '2' | '3' | '4' | '5';
  q8_rating_explanation?: string;
  q9_participate_again: 'Yes' | 'No';
  q10_do_differently?: string;
  q11_other_suggestions?: string;
  q12_open_to_contact: boolean;
  q12_contact_name?: string;
  q12_contact_email?: string;
  q12_contact_phone?: string;
}

/** These survey rows should show no value in Satisfaction Guidelines (grid + export). */
const SURVEY_IDS_BLANK_SATISFACTION = new Set([54, 56]);

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
  only2025?: boolean;
}

const DealerSurveysScreen = () => {
  const localizationService = useLocalization();
  const { ui, login } = useStores();
  const { d4ur } = useServices();

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);

  const [dealerSurveys, setDealerSurveys] = useState<DealerSurveyResponse[]>([]);
  const [loadingSurveys, setLoadingSurveys] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedSurveyYear, setSelectedSurveyYear] = useState<number>(2026);
  const [dataState, setDataState] = useState<State>({
    take: 20,
    skip: 0,
    sort: [{ field: 'timestamp', dir: 'desc' }]
  });

  const getDealerSurveys = async () => {
    setLoadingSurveys(true);
    setErrorMessage('');
    
    try {
      const ret = await d4ur.getDealerSurveys();
      
      // Check if the response has the expected structure
      if (ret && (ret as any).result) {
        const surveyData = (ret as any).result;
        
        // Ensure we have an array
        if (Array.isArray(surveyData)) {
          setDealerSurveys(surveyData);
        } else if (surveyData && typeof surveyData === 'object') {
          // If it's an object, try to extract the array from it
          const dataArray = surveyData.data || surveyData.surveys || Object.values(surveyData);
          if (Array.isArray(dataArray)) {
            setDealerSurveys(dataArray);
          } else {
            setErrorMessage('Survey data is not in expected format');
            setDealerSurveys([]); // Set empty array as fallback
          }
        } else {
          setErrorMessage('No survey data found');
          setDealerSurveys([]); // Set empty array as fallback
        }
      } else if (ret && Array.isArray(ret)) {
        // Handle case where the response is directly an array
        setDealerSurveys(ret);
      } else {
        setErrorMessage('No survey data found');
        setDealerSurveys([]); // Set empty array as fallback
      }
    } catch (error) {
      console.error('Error fetching dealer surveys:', error);
      setErrorMessage('Failed to load dealer surveys');
      setDealerSurveys([]); // Set empty array as fallback
    } finally {
      setLoadingSurveys(false);
    }
  };

  useEffect(() => {
    getDealerSurveys();
  }, []);

  useEffect(() => {
    if (dealerSurveys.length === 0) return;
    const measureWidth = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) setScrollbarWidth(gridContent.scrollWidth);
    };
    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
    };
    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.addEventListener('scroll', syncTopScrollbar);
    });
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [dealerSurveys.length]);

  const onDataStateChange = (event: any) => {
    setDataState(event.dataState);
  };

  // Ensure dealerSurveys is always an array
  const safeDealerSurveys = Array.isArray(dealerSurveys) ? dealerSurveys : [];
  // Filter surveys by selected year (from timestamp)
  const surveysForYear = safeDealerSurveys.filter(
    (s) => new Date(s.timestamp).getFullYear() === selectedSurveyYear
  );
  const surveysForYearExport = useMemo(
    () =>
      surveysForYear.map((s) => ({
        ...s,
        q6_satisfaction_guidelines: SURVEY_IDS_BLANK_SATISFACTION.has(s.survey_id)
          ? undefined
          : s.q6_satisfaction_guidelines,
      })),
    [surveysForYear]
  );
  // For 2026, these questions are not part of the dealer survey, so hide the corresponding admin columns.
  const hide2026AffiliateAndPartnerColumns = selectedSurveyYear === 2026;

  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'survey_id', field: 'survey_id', title: 'Survey ID' },
    { key: 'timestamp', field: 'timestamp', title: 'Date Submitted' },
    { key: 'q1_planned_with_other_activity', field: 'q1_planned_with_other_activity', title: 'Planned with Other Activity' },
    { key: 'q1_what_event', field: 'q1_what_event', title: 'What Event' },
    { key: 'q1_why_not', field: 'q1_why_not', title: 'Why Not' },
    { key: 'q2_rating_ease_working_charity', field: 'q2_rating_ease_working_charity', title: 'Charity Ease Rating (1-5)' },
    { key: 'q2_rating_explanation', field: 'q2_rating_explanation', title: 'Ease Explanation' },
    { key: 'q3_program_guides', field: 'q3_program_guides', title: 'Program Guides' },
    { key: 'q3_downloadable_materials', field: 'q3_downloadable_materials', title: 'Downloadable Materials' },
    { key: 'q3_kit_materials', field: 'q3_kit_materials', title: 'Kit Materials' },
    { key: 'q3_program_giveaways', field: 'q3_program_giveaways', title: 'Program Giveaways' },
    { key: 'q3_other_support', field: 'q3_other_support', title: 'Other Support' },
    { key: 'q3_other_explanation', field: 'q3_other_explanation', title: 'Other Support Explanation' },
    { key: 'q4_additional_support', field: 'q4_additional_support', title: 'Additional Support' },
    { key: 'q5_fundraising_goal_support', field: 'q5_fundraising_goal_support', title: 'Fundraising Support' },
    { key: 'q6_satisfaction_guidelines', field: 'q6_satisfaction_guidelines', title: 'Satisfaction Guidelines (1-5)' },
    { key: 'q6_rating_local_affiliate', field: 'q6_rating_local_affiliate', title: 'Affiliate Rating', only2025: true },
    { key: 'q6_rating_explanation', field: 'q6_rating_explanation', title: 'Affiliate Explanation', only2025: true },
    { key: 'q7_challenges_benefits', field: 'q7_challenges_benefits', title: 'Partner Challenges/Benefits', only2025: true },
    { key: 'q8_overall_value_rating', field: 'q8_overall_value_rating', title: 'Value Rating (1-5)' },
    { key: 'q8_rating_explanation', field: 'q8_rating_explanation', title: 'Value Explanation' },
    { key: 'q9_participate_again', field: 'q9_participate_again', title: 'Participate Again' },
    { key: 'q10_do_differently', field: 'q10_do_differently', title: 'Do Differently' },
    { key: 'q11_other_suggestions', field: 'q11_other_suggestions', title: 'Other Suggestions' },
    { key: 'q12_open_to_contact', field: 'q12_open_to_contact', title: 'Open to Contact' },
    { key: 'q12_contact_name', field: 'q12_contact_name', title: 'Contact Name' },
    { key: 'q12_contact_email', field: 'q12_contact_email', title: 'Contact Email' },
    { key: 'q12_contact_phone', field: 'q12_contact_phone', title: 'Contact Phone' },
  ];

  const availableExportColumnDefs = exportColumnDefs.filter(
    (col) => !col.only2025 || !hide2026AffiliateAndPartnerColumns
  );

  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );

  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAllExportColumns = () => setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  const clearAllExportColumns = () => setSelectedExportColumns([]);

  const selectedExportColumnDefs = availableExportColumnDefs.filter((col) =>
    selectedExportColumns.includes(col.key)
  );
  const dataResult: DataResult = process(surveysForYear, dataState);

  const surveyYearOptions = [
    { label: "2026", value: 2026 },
    { label: "2025", value: 2025 },
  ];

  const formatTimestamp = (timestamp: string) => {
    return moment(timestamp).format('MMM DD, YYYY h:mm A');
  };

  const formatBoolean = (value: boolean) => {
    return value ? 'Yes' : 'No';
  };

  const formatSupportItems = (survey: DealerSurveyResponse) => {
    const items = [];
    if (survey.q3_program_guides) items.push('Program Guides');
    if (survey.q3_downloadable_materials) items.push('Downloadable Materials');
    if (survey.q3_kit_materials) items.push('Kit Materials');
    if (survey.q3_program_giveaways) items.push('Program Giveaways');
    if (survey.q3_other_support) {
      // If q3_other_support is "1", display "Other Support" instead
      if (survey.q3_other_support === "1") {
        items.push('Other Support');
      } else {
        items.push(survey.q3_other_support);
      }
    }
    return items.join(', ') || 'None';
  };

  return (
    <div id="Dashboard" className="dashboard-page main-content">
      <div className="card-container grid">
        {/* Top row: title + dropdown on left, stat cards in the middle */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "24px", marginBottom: "16px", marginTop: "8px", flexWrap: "wrap" }}>
          <div style={{ flexShrink: 0, minWidth: "280px", marginRight: "400px" }}>
            <h3 className="card-title" style={{ marginBottom: "8px" }}>
              {login.user?.credentials.display_name}: Dealer Survey Responses
            </h3>
            <div>
              <label style={{ display: "block", marginBottom: "6px", fontWeight: "bold" }}>
                Survey year
              </label>
              <DropDownList
                data={surveyYearOptions}
                dataItemKey="value"
                textField="label"
                value={surveyYearOptions.find((o) => o.value === selectedSurveyYear) ?? surveyYearOptions[0]}
                onChange={(e) => setSelectedSurveyYear(e.target.value?.value ?? 2026)}
                style={{ width: "120px" }}
              />
            </div>
          </div>
          <div className="event-stats-container" style={{ flex: 1, display: "flex", justifyContent: "center", gap: "10px", flexWrap: "nowrap", minWidth: "0" }}>
          <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "200px", flexShrink: 0 }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>Survey Responses ({selectedSurveyYear})</h4>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Total:</span>
              <strong>{surveysForYear.length}</strong>
            </div>
          </div>
          <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "200px", flexShrink: 0 }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>Would Participate Again</h4>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Yes:</span>
              <strong>{surveysForYear.filter(survey => survey.q9_participate_again === 'Yes').length}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>No:</span>
              <strong>{surveysForYear.filter(survey => survey.q9_participate_again === 'No').length}</strong>
            </div>
          </div>
          <div className="stat-card" style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "200px", flexShrink: 0 }}>
            <h4 style={{ margin: "0 0 8px 0", fontSize: "16px", color: "#444" }}>Average Ratings</h4>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Ease (1-5):</span>
              <strong>{surveysForYear.length > 0 ? (surveysForYear.reduce((sum, survey) => sum + parseInt(survey.q2_rating_ease_working_charity), 0) / surveysForYear.length).toFixed(1) : '0'}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Value (1-5):</span>
              <strong>{surveysForYear.length > 0 ? (surveysForYear.reduce((sum, survey) => sum + parseInt(survey.q8_overall_value_rating), 0) / surveysForYear.length).toFixed(1) : '0'}</strong>
            </div>
          </div>
          </div>
        </div>

        {/* Messages */}
        {loadingSurveys ? <p style={{ color: 'blue' }}>Loading...</p> : null}
        {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : null}
        
        <div className="card-component">
          <ExcelExport
            data={surveysForYearExport}
            ref={_export}
            fileName={`dealer_surveys_${selectedSurveyYear}_${moment().format('YYYYMMDD-hhmma')}.xlsx`}
          >
            {selectedExportColumnDefs.map((col) => (
              <ExcelExportColumn key={col.key} field={col.field} title={col.title} />
            ))}
          </ExcelExport>

          <div style={{ marginBottom: '12px' }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Choose Excel columns ({selectedExportColumnDefs.length} selected)
              </summary>
              <div style={{ marginTop: '8px' }}>
                <button
                  className="k-button k-button-md k-rounded-md"
                  onClick={selectAllExportColumns}
                  type="button"
                  style={{ marginRight: '8px' }}
                >
                  Select All
                </button>
                <button
                  className="k-button k-button-md k-rounded-md"
                  onClick={clearAllExportColumns}
                  type="button"
                >
                  Clear All
                </button>
              </div>
              <div
                style={{
                  marginTop: '10px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))',
                  gap: '6px 12px',
                }}
              >
                {availableExportColumnDefs.map((col) => (
                  <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      checked={selectedExportColumns.includes(col.key)}
                      onChange={() => toggleExportColumn(col.key)}
                    />
                    <span>{col.title}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>

          <style>{`
            .dealersurveys-grid-wrapper .k-grid-content { overflow-x: scroll; scrollbar-width: none; -ms-overflow-style: none; }
            .dealersurveys-grid-wrapper .k-grid-content::-webkit-scrollbar { display: none; }
            .dealersurveys-top-scrollbar { overflow-x: scroll; overflow-y: hidden; height: 14px; position: sticky; top: 0; z-index: 50; background: #f5f5f5; border-bottom: 1px solid #ddd; }
            .dealersurveys-top-scrollbar::-webkit-scrollbar { height: 8px; }
            .dealersurveys-top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
            .dealersurveys-top-scrollbar::-webkit-scrollbar-thumb { background: #aaa; border-radius: 4px; }
            .dealersurveys-top-scrollbar::-webkit-scrollbar-thumb:hover { background: #666; }
          `}</style>
          <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <div ref={topScrollbarRef} className="dealersurveys-top-scrollbar" onScroll={(e) => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) gc.scrollLeft = e.currentTarget.scrollLeft; }}>
              <div style={{ width: scrollbarWidth, height: 1 }} />
            </div>
            <div ref={gridContentRef} className="dealersurveys-grid-wrapper">
          <Grid
            data={dataResult}
            pageable={true}
            sortable={{ mode: "multiple" }}
            filterable={true}
            onDataStateChange={onDataStateChange}
            resizable={true}
            onColumnResize={() => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) setScrollbarWidth(gc.scrollWidth); }}
          >
            <GridToolbar>
              <button className="k-button k-button-md k-rounded-md" onClick={getDealerSurveys} disabled={loadingSurveys}>
                Refresh Data
              </button>
              <button
                className="k-button k-button-md k-rounded-md"
                onClick={excelExport}
                disabled={selectedExportColumnDefs.length === 0}
              >
                Export to Excel ({selectedExportColumnDefs.length} columns)
              </button>
            </GridToolbar>

            <GridColumn 
              field="survey_id" 
              title="Survey ID" 
              width="100px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="timestamp" 
              title="Date Submitted" 
              width="180px"
              format="{0:E, MMM dd yyyy}"
              filter="date"
              columnMenu={ColumnMenu}
              cell={(props) => (
                <td>{formatTimestamp(props.dataItem.timestamp)}</td>
              )}
            />
            <GridColumn 
              field="q1_planned_with_other_activity" 
              title="Planned with Other Activity" 
              width="250px"
              columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                searchBox: () => null,
              })}
            />
            <GridColumn 
              field="q1_what_event" 
              title="What Event" 
              width="150px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q1_why_not" 
              title="Why Not" 
              width="150px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q2_rating_ease_working_charity" 
              title="Charity Ease Rating (1-5)" 
              width="250px"
              columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                searchBox: () => null,
              })}
            />
            <GridColumn 
              field="q2_rating_explanation" 
              title="Ease Explanation" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q3_support_items" 
              title="Support Items Used" 
              width="200px"
              columnMenu={ColumnMenu}
              cell={(props) => (
                <td>{formatSupportItems(props.dataItem)}</td>
              )}
            />
            <GridColumn 
              field="q3_other_explanation" 
              title="Other Support Explanation" 
              width="400px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q4_additional_support" 
              title="Additional Support" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q5_fundraising_goal_support" 
              title="Fundraising Support" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q6_satisfaction_guidelines" 
              title="Satisfaction Guidelines (1-5)" 
              width="220px"
              columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                searchBox: () => null,
              })}
              cell={(props) => (
                <td>
                  {SURVEY_IDS_BLANK_SATISFACTION.has(props.dataItem.survey_id)
                    ? ''
                    : (props.dataItem.q6_satisfaction_guidelines ?? '')}
                </td>
              )}
            />
            {!hide2026AffiliateAndPartnerColumns ? (
              <>
                <GridColumn 
                  field="q6_rating_local_affiliate" 
                  title="Affiliate Rating" 
                  width="150px"
                  columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                    searchBox: () => null,
                  })}
                />
                <GridColumn 
                  field="q6_rating_explanation" 
                  title="Affiliate Explanation" 
                  width="200px"
                  columnMenu={ColumnMenu}
                />
                <GridColumn 
                  field="q7_challenges_benefits" 
                  title="Partner Challenges/Benefits" 
                  width="250px"
                  columnMenu={ColumnMenu}
                />
              </>
            ) : null}
            <GridColumn 
              field="q8_overall_value_rating" 
              title="Value Rating (1-5)" 
              width="200px"
              columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                searchBox: () => null,
              })}
            />
            <GridColumn 
              field="q8_rating_explanation" 
              title="Value Explanation" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q9_participate_again" 
              title="Participate Again" 
              width="300px"
              columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                searchBox: () => null,
              })}
            />
            <GridColumn 
              field="q10_do_differently" 
              title="Do Differently" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q11_other_suggestions" 
              title="Other Suggestions" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q12_open_to_contact" 
              title="Open to Contact" 
              width="200px"
              columnMenu={ColumnMenuCheckboxFilter(surveysForYear, {
                searchBox: () => null,
              })}
              cell={(props) => (
                <td>{formatBoolean(props.dataItem.q12_open_to_contact)}</td>
              )}
            />
            <GridColumn 
              field="q12_contact_name" 
              title="Contact Name" 
              width="150px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q12_contact_email" 
              title="Contact Email" 
              width="200px"
              columnMenu={ColumnMenu}
            />
            <GridColumn 
              field="q12_contact_phone" 
              title="Contact Phone" 
              width="150px"
              columnMenu={ColumnMenu}
            />
          </Grid>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(DealerSurveysScreen);
